// src/follower/components/OpenTradesPanel.jsx
// Phase 4 — pendingOrders wired. Order status indicators per card.
// Section C of myTrades page.

import React, { useState, useEffect } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    border2:"#243548",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso) {
    if (!iso) return "--";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "America/New_York" }) + " ET";
}

function isPendingFill(position) {
    return position.entry_fill_price === 0 && position.status === "open";
}

// Render explanation string:
// First line = headline, remaining "  - " lines = bullets
function ExplanationText({ text }) {
    if (!text) return null;
    const lines = text.split("\n");
    const headline = lines[0];
    const bullets  = lines.slice(1).filter(l => l.trim());
    return (
        <div>
            <div style={{ fontFamily: FONT, fontSize: 10, color: C.body, marginBottom: bullets.length ? 6 : 0 }}>
                {headline}
            </div>
            {bullets.map((line, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 3 }}>
                <div style={{ width: 3, height: 3, borderRadius: "50%", background: C.cyan, marginTop: 5, flexShrink: 0 }} />
          <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, lineHeight: 1.6 }}>
            {line.replace(/^\s*-\s*/, "")}
          </span>
            </div>
                ))}
        </div>
    );
}

// ─── Order status badge ───────────────────────────────────────────────────────
//
// orderEvent shape: { status, master_position_id, ... } from servantOrderEvent

function OrderBadge({ orderEvent, onClear }) {
    if (!orderEvent) return null;
    const { status } = orderEvent;

    if (status === "pending") {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "3px 9px", borderRadius: 3,
                border: `1px solid ${C.amber}40`, background: C.amber + "10",
                fontFamily: FONT, fontSize: 8, color: C.amber, letterSpacing: ".8px",
                flexShrink: 0,
            }}>
                <span style={{ display: "inline-block", animation: "spin 1s linear infinite", fontSize: 9 }}>⟳</span>
                ORDER PENDING
            </div>
        );
    }

    if (status === "filled") {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "3px 9px", borderRadius: 3,
                border: `1px solid ${C.green}40`, background: C.green + "10",
                fontFamily: FONT, fontSize: 8, color: C.green, letterSpacing: ".8px",
                flexShrink: 0,
            }}>
                ✓ FILLED
            </div>
        );
    }

    if (status === "stale") {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "3px 9px", borderRadius: 3,
                border: `1px solid ${C.amber}40`, background: C.amber + "10",
                fontFamily: FONT, fontSize: 8, color: C.amber, letterSpacing: ".8px",
                flexShrink: 0,
            }}>
                ⚠ ORDER STALE
            </div>
        );
    }

    if (status === "failed" || status === "sell_failed") {
        const label = status === "sell_failed" ? "EXIT FAILED" : "ORDER FAILED";
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "3px 9px", borderRadius: 3,
                border: `1px solid ${C.red}40`, background: C.red + "10",
                fontFamily: FONT, fontSize: 8, color: C.red, letterSpacing: ".8px",
                flexShrink: 0,
            }}>
                ✕ {label}
                <button
                    onClick={onClear}
                    style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", padding: 0, fontFamily: FONT, fontSize: 9, lineHeight: 1, opacity: 0.7 }}
                >
                    ✕
                </button>
            </div>
        );
    }

    return null;
}

// ─── Position card ────────────────────────────────────────────────────────────

function PositionCard({ position, explanation, orderEvent, onClearOrder }) {
    const [expanded, setExpanded] = useState(false);
    const pending   = isPendingFill(position);
    const isCall    = position.option_type === "call";
    const typeColor = isCall ? C.green : C.red;
    const typeLabel = isCall ? "CALL" : "PUT";

    // Auto-clear "filled" indicator after 4s
    useEffect(() => {
        if (orderEvent?.status === "filled") {
            const t = setTimeout(() => onClearOrder?.(), 4000);
            return () => clearTimeout(t);
        }
    }, [orderEvent, onClearOrder]);

    // Card border reflects order state
    const orderStatus  = orderEvent?.status;
    const borderColor  =
        orderStatus === "failed" || orderStatus === "sell_failed" ? C.red + "80" :
            orderStatus === "stale"  ? C.amber + "60" :
                orderStatus === "filled" ? C.green + "60" :
                    C.border;

    return (
        <div style={{
      background:   C.bg2,
      border:       `1px solid ${borderColor}`,
      borderRadius: 8,
      overflow:     "hidden",
      transition:   "border-color .3s",
    }}>

            {/* ── Main row ── */}
            <div style={{
        padding:  "14px 18px",
        display:  "flex",
        alignItems: "center",
        gap:      16,
        flexWrap: "wrap",
      }}>

                {/* Type badge */}
                <div style={{
          padding:      "3px 10px",
          borderRadius: 3,
          border:       `1px solid ${typeColor}30`,
          background:   typeColor + "12",
          fontFamily:   FONT,
          fontSize:     9,
          fontWeight:   500,
          letterSpacing:"1px",
          color:        typeColor,
          flexShrink:   0,
        }}>
                    {typeLabel}
                </div>

                {/* Contract symbol */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 160 }}>
          <span style={{ fontFamily: FONT, fontSize: 11, color: C.text, letterSpacing: ".3px" }}>
            {position.contract_symbol}
          </span>
          <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: ".5px" }}>
            {formatTime(position.open_at)}
          </span>
                    {/* Master status badge — open positions only */}
                    {position.status === "open" && position.master_open !== undefined && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                        <div style={{
                                width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                                background: position.master_open ? C.green : C.amber,
                                boxShadow: `0 0 5px ${position.master_open ? C.green : C.amber}`,
                            }} />
                            <span style={{ fontFamily: FONT, fontSize: 8, color: position.master_open ? C.green : C.amber, letterSpacing: ".3px" }}>
                                {position.master_open ? "Master: Holding" : "Master: Exited — close incoming"}
                            </span>
                    </div>
                        )}
                    {/* Holding since — only when open_at is a non-empty string */}
                    {position.status === "open" && position.open_at && (
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: ".3px" }}>
                            {"Holding since " + new Date(position.open_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}
                        </span>
                        )}
                </div>

                {/* Qty */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Qty</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>{position.qty}</span>
                </div>

                {/* Entry fill */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Entry Fill</span>
                    {pending ? (
                    <span style={{ fontFamily: FONT, fontSize: 10, color: C.amber, letterSpacing: ".3px" }}>Awaiting fill</span>
                        ) : (
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>${position.entry_fill_price.toFixed(2)}</span>
                        )}
                </div>

                {/* Underlying entry → target ✕ stop */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Underlying</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 11 }}>
                        <span style={{ color: C.body }}>{position.underlying_entry.toFixed(2)}</span>
                        <span style={{ color: C.meta }}>→</span>
                        <span style={{ color: C.green }}>{position.underlying_target.toFixed(2)}</span>
                        <span style={{ color: C.meta }}>✕</span>
                        <span style={{ color: C.red }}>{position.underlying_stop.toFixed(2)}</span>
                    </div>
                </div>

                {/* Unrealized P&L — not streamed in MVP */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Unrealized P&L</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.meta }}>--</span>
                </div>

                {/* Order status badge + expand toggle — pushed right */}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <OrderBadge orderEvent={orderEvent} onClear={onClearOrder} />
                    {explanation?.entry_explanation && (
                        <button
                        onClick={() => setExpanded(v => !v)}
                        style={{
                        fontFamily:    FONT,
                        fontSize:      8,
                        letterSpacing: "1px",
                        color:         expanded ? C.cyan : C.meta,
                        background:    "transparent",
                        border:        `1px solid ${expanded ? C.cyan + "40" : C.border}`,
                        borderRadius:  3,
                        padding:       "3px 9px",
                        cursor:        "pointer",
                        transition:    "color .15s, border-color .15s",
                        flexShrink:    0,
                        }}
                        >
                        {expanded ? "HIDE ▲" : "WHY ▼"}
                        </button>
                        )}
                </div>

            </div>

            {/* ── Explanation drawer ── */}
            {expanded && explanation?.entry_explanation && (
                <div style={{
                borderTop: `1px solid ${C.border}`,
                padding:   "12px 18px",
                background: C.bg1,
                }}>
                <div style={{ fontFamily: FONT, fontSize: 8, color: C.cyan, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
                Entry Reasoning
                </div>
                <ExplanationText text={explanation.entry_explanation} />
                </div>
                )}

        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
//
// Props:
//   positions     — array from mockStatus.open / GET /follower/status
//   explanations  — dict keyed by master_position_id from mockExplanations
//   pendingOrders — dict keyed by master_position_id from servantOrderEvent

export default function OpenTradesPanel({ positions, explanations, pendingOrders = {} }) {
    // Local dismissed set — lets user clear failed/stale badges without affecting socket state
    const [dismissed, setDismissed] = useState({});

    const handleClearOrder = (id) => setDismissed(prev => ({ ...prev, [id]: true }));
    return (
        <>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

        <div style={{
        background:   C.bg1,
        border:       `1px solid ${C.border}`,
        borderRadius: 10,
        overflow:     "hidden",
      }}>

            {/* ── Panel header ── */}
            <div style={{
          padding:        "14px 24px",
          borderBottom:   `1px solid ${C.border}`,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
        }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 3, height: 16, borderRadius: 2, background: `linear-gradient(180deg, ${C.green}, ${C.green}40)` }} />
            <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
              Open Trades
            </span>
                </div>
                {positions.length > 0 && (
                <div style={{
              fontFamily:    FONT,
              fontSize:      9,
              color:         C.cyan,
              background:    C.cyan + "12",
              border:        `1px solid ${C.cyan}30`,
              borderRadius:  3,
              padding:       "2px 8px",
              letterSpacing: "1px",
            }}>
                    {positions.length} OPEN
                </div>
                    )}
            </div>

            {/* ── Body ── */}
            <div style={{ padding: "16px" }}>
                {positions.length === 0 ? (

                    // Empty state
                <div style={{
              padding:    "40px 24px",
              textAlign:  "center",
              display:    "flex",
              flexDirection: "column",
              alignItems: "center",
              gap:        10,
            }}>
                    <div style={{
                width:        40,
                height:       40,
                borderRadius: "50%",
                border:       `1px solid ${C.border}`,
                display:      "grid",
                placeItems:   "center",
                marginBottom: 4,
              }}>
                        <div style={{ width: 12, height: 12, borderRadius: "50%", border: `1px solid ${C.meta}`, opacity: .4 }} />
                    </div>
              <span style={{ fontFamily: FONT, fontSize: 10, color: C.meta, letterSpacing: ".3px" }}>
                No open trades — waiting for master signal
              </span>
                </div>

                    ) : (

                    // Position cards
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {positions.map(pos => (
                    <PositionCard
                        key={pos.master_position_id}
                        position={pos}
                        explanation={explanations?.[pos.master_position_id]}
                        orderEvent={dismissed[pos.master_position_id] ? null : pendingOrders[pos.master_position_id]}
                        onClearOrder={() => handleClearOrder(pos.master_position_id)}
                    />
                        ))}
                </div>

                    )}
            </div>

        </div>
        </>
    );
}